import { KatButton, KatInput } from '@amzn/katal-react';
import { FC } from 'react';
import { UrlInputProps } from '.';
import styles from './UrlInput.module.css';
import { AssetStatus } from 'src/types';

/**
 * Component for displaying URLs with truncation for long URLs
 */
const UrlDisplay: FC<{ url: string }> = ({ url }) => (
  <div data-testid="url-container">
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="url-display"
      aria-label={`Visit ${url}`}
    >
      {url.slice(0, 15)}...
    </a>
  </div>
);

export const UrlInput: FC<UrlInputProps> = ({
  url,
  status,
  onChange,
  onFetch,
  onClear,
  isValidUrl,
  isFetching,
  disabled = false,
}) => {
  const shouldDisableClear = !url || url.trim().length === 0;

  return (
    <div className={styles.container} data-testid="url-input-container">
      {status === AssetStatus.PENDING_INPUT ? (
        <KatInput
          value={url}
          onInput={onChange}
          onChange={onChange}
          placeholder="Enter URL"
          type="url"
          disabled={disabled}
          onKeydown={(e: KatInput.KeydownEvent) => {
            // Safely check if key property exists before using it
            const key = 'key' in e ? e.key : undefined;
            if (key === 'Enter' && isValidUrl && !isFetching && !disabled) {
              // Prevent form submission if component is within a form
              if ('preventDefault' in e) {
                e.preventDefault();
              }
              onFetch();
            }
          }}
          data-testid="url-input"
        />
      ) : (
        <UrlDisplay url={url} />
      )}

      <KatButton
        onClick={onFetch}
        disabled={disabled || !isValidUrl || isFetching}
        label="Fetch"
        variant="primary"
        data-testid="fetch-button"
        aria-busy={isFetching}
      />

      <KatButton
        onClick={onClear}
        disabled={disabled || shouldDisableClear || isFetching}
        label="Clear"
        variant="warning"
        data-testid="clear-button"
        aria-label="Clear URL input"
      />
    </div>
  );
};
